/** Pinia Store para la gestión de Carátula */
import { defineStore } from 'pinia';
import { getCurrentInstance } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { useAuthStore } from '@/pinia/auth.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { getNestedValue } from '@/helpers/sort';
import * as service from '@/services';
import html2pdf from 'html2pdf.js';

const $toast = useToast();
const endPointPromissoryNotes = "/api/PromissoryNotes";

export const usePromissoryNoteStore = defineStore('promissoryNote', {
    /**
     * Estado central del store.
     */
    state: () => ({
        /**Modal */
        isEdit: false,
        isModalOpen: false,
        loanApplicationsOptions: [],
        selectedApplication: null,
        promissoryNoteContent: null,
        errors: null,
        /**Formulario */
        form: {},
        nameFilePDF: "PAGARÉ.pdf",
        /**Datos de tabla */
        sortKey: '',
        sortOrder: 1, // 1 for ascending, -1 for descending
        itemsPerPage: 10, //Por default 10 por pagina
        currentPage: 1,
        searchString: '',
        promissoryNote: [],
        statusPromissoryNote: 21, //Dato default,
        selectedAmortizationIndex: null,
        selectedAmortization: null
    }),
    /**
     * Getters para filtrar, ordenar y paginar las carpatulas.
     */
    getters: {
        /**
         * Devuelve las carátulas filtradas por el texto de búsqueda.
         */
        filteredS: (state) => {
            if (!state.searchString) return state.promissoryNote;

            //#region control de fecha string
            const instance = getCurrentInstance();
            const $filters = instance?.appContext.config.globalProperties.$filters;
            //#endregion control de fecha string

            const searchLower = state.searchString.toLowerCase();
            return state.promissoryNote.filter(promissoryNote => {
                const firstNameProspect = promissoryNote?.prospects?.generalInformation?.firstName ? promissoryNote?.prospects?.generalInformation?.firstName?.toLowerCase() : '';
                const lastNameProspect = promissoryNote?.prospects?.generalInformation?.lastName ? promissoryNote?.prospects?.generalInformation?.lastName?.toLowerCase() : '';
                const mothersMaidenNameProspect = promissoryNote?.prospects?.generalInformation?.mothersMaidenName ? promissoryNote?.prospects?.generalInformation?.mothersMaidenName?.toLowerCase() : '';
                const nameProspect = promissoryNote?.prospects?.generalInformation?.businessName === null ?
                    `${firstNameProspect} ${lastNameProspect} ${mothersMaidenNameProspect}`
                    :
                    promissoryNote?.prospects?.generalInformation?.businessName ? promissoryNote?.prospects?.generalInformation?.businessName?.toLowerCase() : '';

                return (
                    promissoryNote?.promissoryNotes?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    promissoryNote?.loanApplications?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    promissoryNote?.prospects?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    promissoryNote?.quotations?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    nameProspect.includes(searchLower) ||
                    promissoryNote.prospects?.personTypes?.name?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(promissoryNote?.promissoryNotes.createdAt)?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(promissoryNote?.promissoryNotes?.updatedAt)?.toLowerCase().includes(searchLower)
                );
            });
        },
        /**
         * Ordena las carátula según la clave de orden actual.
         */
        sortedS: (state) => {
            // Logica de ordenamiento
            let sortedList = [...state.filteredS];
            if (state.sortKey) {
                sortedList.sort((a, b) => {

                    // Caso especial para el nombre
                    if (state.sortKey === 'name') {
                        const getNameForSort = (item) => {
                            if (!item) return '';

                            if (item.prospects.generalInformation.businessName === null) {
                                return `${item.prospects?.generalInformation?.firstName || ''} ${item.prospects?.generalInformation?.lastName || ''} ${item.prospects?.generalInformation?.mothersMaidenName || ''}`.toLowerCase();
                            } else {
                                return (item.prospects?.generalInformation?.businessName || '').toLowerCase();
                            }
                        };

                        const aName = getNameForSort(a);
                        const bName = getNameForSort(b);
                        if (aName < bName) return -1 * state.sortOrder;
                        if (aName > bName) return 1 * state.sortOrder;
                        return 0;
                    }

                    // Ordenamiento normal para otras columnas
                    let aValue = getNestedValue(a, state.sortKey);
                    let bValue = getNestedValue(b, state.sortKey);

                    if (aValue < bValue) return -1 * state.sortOrder;
                    if (aValue > bValue) return 1 * state.sortOrder;
                    return 0;
                });
            }
            return sortedList;
        },
        /**
         * Aplica paginación a la lista ordenada.
         */
        paginatedS: (state) => {
            if (!state.sortedS || state.sortedS.length === 0) return [];
            let start = (state.currentPage * state.itemsPerPage) - state.itemsPerPage;
            return state.sortedS.slice(start, start + state.itemsPerPage);
        }
    },
    /**
     * Acciones asincrónicas y mutaciones del store.
     */
    actions: {
        /**
         * Obtiene las opciones de solicitudes disponibles para llenar el combo de selección de la modal de pagare.
         * http://localhost:5149/api/PromissoryNotes/loanApplications/detail/1 ${userAuth.user.companyId}
         */
        async filterLoanApplications() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.promissoryNotesApi.get(`${endPointPromissoryNotes}/loanApplications/detail/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.loanApplicationsOptions = data.data;
                }
                useLoadingStore().loading = false;
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Crea un nuevo regisro de pagaré.
         * @param {*} payload 
         * @returns 
         * http://localhost:5149/api/PromissoryNotes
        */
        async createPromissoryNote(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.promissoryNotesApi.post(endPointPromissoryNotes, payload);
                if (data && data.status) {
                    $toast.success(getLocaleMessage('promissoryNote', 'creatingSuccess'));
                    this.form.promissoryNotes = data.data;
                    this.selectedApplication.promissoryNotes = data.data;
                    this.promissoryNote.unshift(this.selectedApplication);
                    return data;
                } else {
                    $toast.success(getLocaleMessage('promissoryNote', 'creatingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Obtiene todos los pagarés registrados.
         *  http://localhost:5149/api/PromissoryNotes/company/1
         * http://localhost:5149/api/PromissoryNotes/detail/company/1
        */
        async fetchPromissoryNote() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.promissoryNotesApi.get(`${endPointPromissoryNotes}/detail/company/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.promissoryNote = data.data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Actualiza un registro de pagaré existente.
         * http://localhost:5149/api/PromissoryNotes
        */
        async updatePromissoryNote(payload) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.promissoryNotesApi.put(`${endPointPromissoryNotes}`, payload);
                if (data && data.status) {
                    const index = this.promissoryNote.findIndex(c => c.promissoryNotes.id === data.id);
                    if (index !== -1) {
                        this.promissoryNote.splice(index, 1, this.form);
                    }
                    $toast.success(getLocaleMessage('promissoryNote', 'editingSuccess'));
                    return data;
                } else {
                    $toast.success(getLocaleMessage('promissoryNote', 'editingError'));
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Abre el modal para crear nuevo registro de carátula.
         * Acción por el boton Generar
         * Abrir modal de creación isEdit = false
         */
        async openModal() {
            this.isModalOpen = true;
            this.isEdit = false;
            await this.filterLoanApplications(); //Refrescar los datos del combo de selección
        },
        /**
         * Cierra el modal y limpia estados relacionados.
         */
        async closeModal() {
            this.isEdit = false;
            this.isModalOpen = false;
            this.loanApplicationsOptions = [];
            this.selectedApplication = null;
            this.promissoryNoteContent = null;
            this.errors = null;
            this.form = {};
            // Resetear el índice de amortización seleccionado
            this.selectedAmortizationIndex = null;
            this.selectedAmortization = null;
            await this.fetchPromissoryNote(); //Refrescar datos de las tablas
        },
        /**
         * Maneja errores en la comunicación con la API.
         */
        handleError(error) {
            this.errors = error;
            if (error.response) {
                console.log("Inspec handle ERROR IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'responseError');
                $toast.error(msg);
            } else if (error.request) {
                console.log("Inspec handle ERROR ELSE IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'networkError');
                $toast.error(msg);
            } else {
                console.log("Inspec handle ERROR ELSE: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'generalError');
                $toast.error(msg);
            }
        },
        /**
        * Cambia el orden de las columnas en la tabla.
        */
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortOrder = 1;
            }
            this.sortKey = key;
            this.currentPage = 1; // Resetear a la primera página al ordenar
        },
        /**
         * Establece la página actual del paginador.
         */
        setCurrentPage(page) {
            this.currentPage = page;
        },
        /**
         * Carga los datos de un carátula existente para edición.
         * Abrir modal de edición isEdit = true
         */
        editData(formEdit) {
            this.isEdit = true;
            this.selectedApplication = formEdit;
            this.form = formEdit;
        },
        /**
         * Acción para guarda los datos del formulario, ya sea creando o actualizando.
        */
        async saveData() {
            try {
                useLoadingStore().loading = true;
                let response;
                if (this.isEdit) {
                    response = await this.updatePromissoryNote(this.form.promissoryNotes);
                } else {
                    let numberPhone = this.form?.prospects?.generalInformation?.cellPhone ||
                        this.form?.prospects?.generalInformation?.homePhone ||
                        this.form?.prospects?.generalInformation?.officePhone ||
                        '';

                    let payload = {
                        loanApplicationId: this.form.loanApplications.id,
                        statusId: this.statusPromissoryNote,
                        promissoryNumber: this.form?.promissoryNotes?.promissoryNumber,
                        subscribedName: this.form?.promissoryNotes.subscribedName ?? '',
                        amount: this.form?.quotations?.paymentPeriodAmount ?? 0, //this.form.promissoryNotes.amount
                        paymentDate: this.form?.quotations?.disbursementDate ?? null,
                        referenceRate: this.form?.quotations?.annualRateWithVat ?? 0,
                        penaltyRate: this.form?.quotations?.periodRateWithVat ?? 0,
                        subscriptionAddress: `${this.form.prospects?.addressAccredited.street} ${this.form.prospects?.addressAccredited.exteriorNumber} ${this.form.prospects?.addressAccredited.interiorNumber} ${this.form.prospects?.addressAccredited.district}` ?? '',
                        subscriptionDate: this.form?.promissoryNotes?.subscriptionDate ?? null,
                        subscriberName: this.form.prospects?.generalInformation.businessName === null ? `${this.form.prospects?.generalInformation?.firstName || ''} ${this.form.prospects?.generalInformation?.lastName || ''} ${this.form.prospects?.generalInformation?.mothersMaidenName || ''}` : `${this.form.prospects?.generalInformation?.businessName || ''}`,
                        subscriberAddress: `${this.form?.prospects?.prospects?.addressAccredited?.street || ''} ${this.form?.prospects?.prospects?.addressAccredited?.exteriorNumber || ''} ${this.form?.prospects?.prospects?.addressAccredited?.interiorNumber || ''} ${this.form?.prospects?.prospects?.addressAccredited?.district || ''}`,
                        subscriberPhone: numberPhone,
                        subscriberSignature: "",
                        guarantorName: this.form?.promissoryNotes?.guarantorName,
                        guarantorAddress: this.form?.promissoryNotes?.guarantorAddress,
                        guarantorPhone: this.form?.promissoryNotes?.guarantorPhone,
                        guarantorSignature: ""
                    };
                    response = await this.createPromissoryNote(payload);
                }

                if (response && response.status) {
                    this.downloadPDF();
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Funcionalidad para manejar la descarga de un pdf
         * @returns Descarga un PDF
         */
        async downloadPDF() {
            if (!this.promissoryNoteContent) return;

            /**Ocultar el boton de descarga */
            const formActions = document.querySelector('.form-actions');
            if (formActions) formActions.style.display = 'none';

            const element = document.querySelector('.page');
            if (element) element.classList.add('pdf-mode');

            const opt = {
                margin: 5,
                filename: this.nameFilePDF,
                image: { type: 'jpeg', quality: 1 },
                html2canvas: {
                    scale: 2,
                    logging: true,
                    useCORS: true,
                    letterRendering: true,
                    clip: false, // Permite capturar contenido fuera de los límites del viewport
                    ignoreElements: function (element) {
                        return false; // Captura todos los elementos
                    }
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4', // Tamaño A4 en píxeles
                    orientation: 'portrait'
                },
                // Configuración para manejar saltos de página
                pagebreak: {
                    mode: ['avoid-all', 'css', 'legacy']
                }
            };

            try {
                useLoadingStore().loading = true;
                await html2pdf().set(opt).from(this.promissoryNoteContent).save();
            } catch (error) {
                useLoadingStore().loading = false;
                this.handleError(error);
            } finally {
                useLoadingStore().loading = false;

                /**Restableciendo diseños de origen en la modal */
                if (formActions) formActions.style.display = 'block';
                if (element) element.classList.remove('pdf-mode');
            }

            /**
             * Nota: Con esta parte del codigo soluciono el detalle de cuando se mantiene
             * abierto la modal de creación despues dar descargar y este se manda a GUARDAR 
             * si el usuario quiere modificar algun campo y mandar descargar de nuevo con la
             * nueva captura en alguno de los campos no deja porque le marca error de solicitud
             * ya existente.
             * Opción 1:
             * Comportamiento: Una vez creado el registro y como se mantiene la modal abierta
             * por automatico cuando se termina de GUARDAR el registro en la BD este se convierte
             * en una modal de edición. Si ese es el comportamiento adecuado dejar este codigo
             * Opción 2:
             * Comportamiento: Una vez creado el registro y como se mantiene la modal abierta
             * y sigue siendo una creación este al querer editar un dato y de descargar lo sigue
             * reconociendo como una creación y marca error porque dice que ya existe el uso
             * de esa solicitud dentro del proceso de aviso de privacidad. Si ese es el
             * comportamiento adecuado retirar este codigo
             */
            //this.isEdit = true; //indica vuelvelo edición
            //this.policyId = this.privacyPolicies[0].id; //setea el valor del nuevo registro creado
            //this.numReference = this.privacyPolicies[0].loanApplication.referenceNumber; //setea el valor de numero de solicitud
        },
        setPromissoryNoteContent(ref) {
            this.promissoryNoteContent = ref;
        },
        setSelectedAmortization(index) {
            if (this.form?.quotations?.loanAmortizations?.[index]) {
                this.selectedAmortizationIndex = index;
                this.selectedAmortization = this.form.quotations.loanAmortizations[index];
                // Aquí puedes setear otros valores necesarios
                if (this.form.quotations) {
                    // this.form.promissoryNotes.promissoryNumber = index + 1;
                    this.form.promissoryNotes.promissoryNumber = index;
                    //const filterAmortization = this.form?.quotations?.loanAmortizations?.filter((x, indexA) => indexA === index);
                    this.form.quotations.disbursementDate = this.form.quotations.loanAmortizations?.[index].startDate;
                    this.form.quotations.paymentPeriodAmount = this.form.quotations.loanAmortizations?.[index].installmentWithVat;
                }
            }
        }
    }
});